import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { BlogService } from '../../services/blog.service';
import { Blog } from '../../core/models/blog.model';
import { NewsletterComponent } from '../../shared/components/newsletter/newsletter.component';

@Component({
  selector: 'app-blog-detail',
  standalone: true,
  imports: [RouterLink, CommonModule, NewsletterComponent],
  templateUrl: './blog-detail.component.html',
  styleUrl: './blog-detail.component.css'
})
export class BlogDetailComponent implements OnInit {
  blog: Blog | null = null;
  loading = true;
  error: string | null = null;
  relatedBlogs: Blog[] = [];
  currentSlug: string = '';

  constructor(
    private blogService: BlogService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const slug = params['slug'];
      if (slug) {
        this.currentSlug = slug;
        this.loadBlog(slug);
        this.loadRelatedBlogs();
      } else {
        this.error = 'Blog bulunamadı.';
        this.loading = false;
      }
    });
  }

  reloadBlog(): void {
    if (this.currentSlug) {
      this.loadBlog(this.currentSlug);
    }
  }

  loadBlog(slug: string): void {
    this.loading = true;
    this.error = null;

    this.blogService.getBlogBySlug(slug).subscribe({
      next: (blog) => {
        this.blog = blog;
        this.loading = false;
      },
      error: (error) => {
        this.error = error.status === 404 
          ? 'Blog yazısı bulunamadı.' 
          : 'Blog yazısı yüklenirken bir hata oluştu.';
        this.loading = false;
      }
    });
  }

  loadRelatedBlogs(): void {
    this.blogService.getPublishedBlogs().subscribe({
      next: (blogs) => {
        // Mevcut blog hariç ilk 4 blogu al (grid düzeni için çift sayı)
        this.relatedBlogs = blogs
          .filter(b => b.slug !== this.currentSlug)
          .slice(0, 4);
      },
      error: (error) => {
      }
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    };
    return date.toLocaleDateString('tr-TR', options);
  }

  getTruncatedTitle(title: string, wordLimit: number = 3): string {
    if (!title) return '';
    const words = title.split(' ');
    if (words.length <= wordLimit) return title;
    return words.slice(0, wordLimit).join(' ') + '...';
  }

  getDefaultImage(index: number): string {
    const images = [
      'assets/img/blog/list-2/1.png',
      'assets/img/blog/list-2/2.png',
      'assets/img/blog/list-2/3.png',
      'assets/img/blog/list-2/4.png',
      'assets/img/blog/list-2/5.png'
    ];
    return images[index % images.length];
  }

  getBlogImage(blog: Blog, index: number): string {
    // Önce coverImage varsa onu kullan
    const coverImageUrl = this.blogService.getImageUrl(blog.coverImage);
    if (coverImageUrl) return coverImageUrl;
    
    // coverImage yoksa default image kullan
    return this.getDefaultImage(index);
  }

  getMainImage(): string {
    if (!this.blog) return 'assets/img/blog/single/1.png';
    
    // Önce coverImage varsa onu kullan
    const coverImageUrl = this.blogService.getImageUrl(this.blog.coverImage);
    if (coverImageUrl) return coverImageUrl;
    
    // coverImage yoksa default image kullan
    return 'assets/img/blog/single/1.png';
  }

  getAdditionalImages(): string[] {
    if (!this.blog) return [];
    
    const images: string[] = [];
    
    const img1 = this.blogService.getImageUrl(this.blog.additionalImage1);
    if (img1) images.push(img1);
    
    const img2 = this.blogService.getImageUrl(this.blog.additionalImage2);
    if (img2) images.push(img2);
    
    return images;
  }

  // Sosyal medya paylaşım metodları
  private getShareUrl(): string {
    if (typeof window !== 'undefined') {
      return window.location.href;
    }
    return '';
  }

  private getShareText(): string {
    return this.blog?.name || 'Blog yazısı';
  }

  shareOnFacebook(): void {
    const url = this.getShareUrl();
    const shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    this.openShareWindow(shareUrl);
  }

  shareOnTwitter(): void {
    const url = this.getShareUrl();
    const text = this.getShareText();
    const shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`;
    this.openShareWindow(shareUrl);
  }

  shareOnWhatsApp(): void {
    const url = this.getShareUrl();
    const text = this.getShareText();
    const shareUrl = `https://wa.me/?text=${encodeURIComponent(text + ' - ' + url)}`;
    this.openShareWindow(shareUrl);
  }

  shareOnInstagram(): void {
    // Instagram web'de direkt paylaşım desteklemiyor
    // Mobilde Instagram uygulamasını aç, değilse Instagram anasayfasını aç
    if (typeof window !== 'undefined' && typeof navigator !== 'undefined') {
      const userAgent = navigator.userAgent || navigator.vendor;
      const url = this.getShareUrl();
      
      // Mobil cihazda Instagram uygulamasını açmayı dene
      if (/android/i.test(userAgent)) {
        window.location.href = `instagram://share?url=${encodeURIComponent(url)}`;
        // Eğer uygulama yoksa web sitesine yönlendir
        setTimeout(() => {
          window.open('https://www.instagram.com/', '_blank');
        }, 1000);
      } else if (/iPad|iPhone|iPod/.test(userAgent)) {
        window.location.href = `instagram://share?url=${encodeURIComponent(url)}`;
        setTimeout(() => {
          window.open('https://www.instagram.com/', '_blank');
        }, 1000);
      } else {
        // Masaüstünde Instagram'a yönlendir
        window.open('https://www.instagram.com/', '_blank');
      }
    }
  }

  private openShareWindow(url: string): void {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
    }
  }
}

